
import React, { useState } from "react";
import { Requirement } from "@/types/project";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface EditRequirementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  requirement: Requirement | null;
  onSave: (requirement: Requirement) => Promise<void>;
}

export function EditRequirementDialog({
  isOpen,
  onClose,
  requirement,
  onSave,
}: EditRequirementDialogProps) {
  const [editedRequirement, setEditedRequirement] = useState<Requirement | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (requirement) {
      setEditedRequirement({ ...requirement });
    } else {
      setEditedRequirement(null);
    }
  }, [requirement]);

  if (!editedRequirement) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedRequirement((prev) => {
      if (!prev) return null;
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editedRequirement) return;

    try {
      setIsSaving(true);
      await onSave(editedRequirement);
      toast.success("Requirement updated successfully");
      onClose();
    } catch (error) {
      console.error("Error saving requirement:", error);
      toast.error("Failed to update requirement");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Requirement</DialogTitle>
            <DialogDescription>
              Make changes to the requirement details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="section" className="text-right">
                Section
              </Label>
              <Input
                id="section"
                name="section"
                value={editedRequirement.section || ""}
                onChange={handleChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subsection" className="text-right">
                Subsection
              </Label>
              <Input
                id="subsection"
                name="subsection"
                value={editedRequirement.subsection || ""}
                onChange={handleChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                Type
              </Label>
              <Input
                id="type"
                name="requirement_type"
                value={editedRequirement.requirement_type || editedRequirement.type || ""}
                onChange={handleChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="text" className="text-right">
                Requirement Text
              </Label>
              <Textarea
                id="text"
                name="requirement_text"
                value={editedRequirement.requirement_text || editedRequirement.text || ""}
                onChange={handleChange}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
